import type {CSSProperties} from 'react';
import {radius, weight} from '../scale.js';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeLeadsReviewLook = 'import-review-check' | 'import-review-dupe' | 'import-review-dupe-on' | 'import-review-all' | 'import-review-more' | 'import-review-keep';
export type WedgeLeadsReviewFieldVariant = 'wedge-review-row' | 'wedge-review-company' | 'wedge-review-tag';
export type WedgeLeadsReviewSelectVariant = 'wedge-review-column' | 'wedge-review-column-skip' | 'wedge-review-dupe' | 'wedge-review-stage';

const REVIEW_LOOKS = new Set<string>(['import-review-check', 'import-review-dupe', 'import-review-dupe-on', 'import-review-all', 'import-review-more', 'import-review-keep']);
const REVIEW_FIELDS = new Set<string>(['wedge-review-row', 'wedge-review-company', 'wedge-review-tag']);
const REVIEW_SELECTS = new Set<string>(['wedge-review-column', 'wedge-review-column-skip', 'wedge-review-dupe', 'wedge-review-stage']);
export const isWedgeLeadsReviewLook = (look: string): look is WedgeLeadsReviewLook => REVIEW_LOOKS.has(look);
export const isWedgeLeadsReviewFieldVariant = (variant: unknown): variant is WedgeLeadsReviewFieldVariant => REVIEW_FIELDS.has(variant as string);
export const isWedgeLeadsReviewSelectVariant = (variant: unknown): variant is WedgeLeadsReviewSelectVariant => REVIEW_SELECTS.has(variant as string);

/* Released review phase of the lead import sheet (prep-leads PipelineImportSheet), off-scale. Every look keeps the `import-` prefix, so the
   import look rule that draws no transparent target covers it (wedgeLeadsImportDrawsHit). The shipped tap size is 34px on a desktop
   pointer and 44px on a phone, so the minimum height, and for the include box the width, stay with the caller. At 34px two dupe pills sit
   6px apart and a 44px region would grow 5px into each neighbor, so no region is drawn. The include box is a clear 44px (34px on desktop)
   square around a 26px check box. Merge all, Skip all and Add all as new are 999px pills (the chosen one takes the accent tint, the accent
   border at 40% and the accent ink); Select all is a bare accent text button; Show more is the ghost box (surface fill, card border,
   secondary ink, 14px type, 44px tall, full width, 10px above); Import the first N is a block pill with a red border at 30%. Each color
   reads the exact legacy role. */
const row = {display: 'inline-flex', alignItems: 'center', justifyContent: 'center'} as const;
const mix = (role: 'accent' | 'red', pct: number) => `color-mix(in srgb, ${wedgeExactRole(role)} ${pct}%, transparent)`;

export function wedgeLeadsReviewStyle(look: WedgeLeadsReviewLook): CSSProperties {
  const r = wedgeExactRole;
  switch (look) {
    case 'import-review-check': return {background: 'transparent', border: 0, ...row, cursor: 'pointer', padding: 0};
    case 'import-review-dupe':
    case 'import-review-dupe-on': {
      const on = look === 'import-review-dupe-on';
      return {
        padding: '7px 12px', borderRadius: radius.pill, fontSize: 12.5, fontWeight: weight.bold, cursor: 'pointer',
        border: `1px solid ${on ? mix('accent', 40) : r('cardBorder')}`, background: on ? mix('accent', 12) : r('bg'), color: on ? r('accent') : r('labelSec'),
      };
    }
    case 'import-review-all': return {
      background: 'none', border: 0, color: r('accent'), fontWeight: weight.bold, fontSize: 12.5, cursor: 'pointer', padding: '4px 8px',
    };
    case 'import-review-more': return {
      minHeight: 44, padding: '12px 14px', borderRadius: radius.lg, background: r('surface'), border: `1px solid ${r('cardBorder')}`, color: r('labelSec'),
      fontWeight: weight.semibold, fontSize: 14, cursor: 'pointer', ...row, gap: 8, width: '100%', marginTop: 10,
    };
    case 'import-review-keep': return {
      display: 'block', marginTop: 8, padding: '7px 12px', borderRadius: radius.pill, border: `1px solid ${mix('red', 30)}`, background: r('bg'),
      color: r('label'), fontWeight: weight.bold, fontSize: 12.5, cursor: 'pointer',
    };
  }
}

/* The three text inputs of the review phase. A row field is the shipped input box (full width, border box, the page fill, a 9px radius,
   8px by 10px of padding, 14px type, no outline, no minimum width) at the tap height the caller sets; the company field is bold. The
   tag field sits on the surface fill with a 10px radius and 10px by 12px of padding. */
export function wedgeLeadsReviewFieldStyle(variant: WedgeLeadsReviewFieldVariant): CSSProperties {
  const r = wedgeExactRole;
  if (variant === 'wedge-review-tag') {
    return {
      width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: radius.md, background: r('surface'), color: r('label'),
      border: `1px solid ${r('cardBorder')}`, fontSize: 14, outline: 'none',
    };
  }
  return {
    width: '100%', boxSizing: 'border-box', background: r('bg'), border: `1px solid ${r('cardBorder')}`, borderRadius: 9, padding: '8px 10px',
    color: r('label'), fontFamily: 'inherit', fontSize: 14, outline: 'none', minWidth: 0, ...(variant === 'wedge-review-company' ? {fontWeight: weight.bold} : {}),
  };
}

/* The three selects of the review phase, each a bare native select. The column select is a 999px pill on the page fill, 12.5px bold, in
   the accent ink with an accent border at 30% (the skip form: the tertiary ink and the card border). The duplicate action select is
   12px with an 8px radius on the page fill. The stage select is full width on the surface fill with a 10px radius and 14px type. The
   caller sets the tap height of the first two. */
export function wedgeLeadsReviewSelectStyle(variant: WedgeLeadsReviewSelectVariant): CSSProperties {
  const r = wedgeExactRole;
  switch (variant) {
    case 'wedge-review-column':
    case 'wedge-review-column-skip': {
      const skip = variant === 'wedge-review-column-skip';
      return {
        fontSize: 12.5, fontWeight: weight.bold, color: skip ? r('labelTer') : r('accent'), background: r('bg'),
        border: `1px solid ${skip ? r('cardBorder') : mix('accent', 30)}`, borderRadius: radius.pill, padding: '4px 8px', cursor: 'pointer',
      };
    }
    case 'wedge-review-dupe': return {fontSize: 12, padding: '4px 8px', borderRadius: radius.sm, background: r('bg'), color: r('label'), border: `1px solid ${r('cardBorder')}`};
    case 'wedge-review-stage': return {
      width: '100%', minHeight: 44, padding: '10px 12px', borderRadius: radius.md, background: r('surface'), color: r('label'),
      border: `1px solid ${r('cardBorder')}`, fontSize: 14,
    };
  }
}
